import type { FC } from 'react';
import * as React from 'react';
import { useMemo } from 'react';
import { Button, Card, Stack, styled, Typography } from '@mui/material';
import type { PaymentMethodDto } from '@pflegenavi/shared/api';
import { DirectDebitStatus } from '@pflegenavi/shared/api';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';
import { useTranslation } from 'react-i18next';

import * as Yup from 'yup';

import type { FormikHelpers } from 'formik';
import { getIn, useFormik } from 'formik';
import { FTextInput, IbanInput } from '@pflegenavi/web-components';
import {
  MandateStatus,
  useSetupDirectDebit,
} from '../../hooks/useSetupDirectDebit';
import { useSetupDirectDebitValidationSchema } from '@pflegenavi/shared/frontend';
import { PaymentInitiationInfo } from './PaymentInitiationInfo';

interface SetupDirectDebitProps {
  status: DirectDebitStatus;
  paymentMethod?: PaymentMethodDto;
}

export interface ISetUpDirectDebitValues {
  name: string;
  email: string;
  iban: string;
}

export const SetupDirectDebit: FC<SetupDirectDebitProps> = ({
  status,
  paymentMethod,
}) => {
  const { familyMember } = useFMContext();
  const { t } = useTranslation();
  const isFormDisabled =
    status === DirectDebitStatus.Active || status === DirectDebitStatus.Pending;

  const validationSchema = Yup.object(useSetupDirectDebitValidationSchema());

  const initialValues: ISetUpDirectDebitValues = useMemo(() => {
    const iban =
      paymentMethod?.country && paymentMethod?.last4
        ? `${paymentMethod.country}**************${paymentMethod.last4}`
        : '';
    return {
      name: paymentMethod?.name ?? familyMember?.name ?? '',
      email: paymentMethod?.email ?? familyMember?.email ?? '',
      iban,
    };
  }, [familyMember?.email, familyMember?.name, paymentMethod]);

  const { mandateStatus, confirm } = useSetupDirectDebit();

  const handleSubmit = (
    values: ISetUpDirectDebitValues,
    actions: FormikHelpers<ISetUpDirectDebitValues>
  ) => {
    actions.setSubmitting(true);
    actions.setSubmitting(false);
    confirm(values);
    actions.resetForm({ values });
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnChange: true,
    validateOnMount: true,
  });

  return (
    <RootContainer>
      <Stack
        direction="column"
        sx={{
          gap: 3,
        }}
      >
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <Typography variant="h5">
            {t('mobile.family-member.direct-debit-form-title')}
          </Typography>
          <PaymentInitiationInfo />
        </Stack>
        <FTextInput
          id="name"
          name="name"
          label={t('mobile.family-member.full-name-field')}
          placeholder={t('mobile.family-member.full-name-field')}
          fullWidth
          disabled={isFormDisabled}
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
        <FTextInput
          id="email"
          name="email"
          label={t('mobile.family-member.email-field')}
          placeholder={t('mobile.family-member.email-field')}
          fullWidth
          disabled={isFormDisabled}
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <IbanInput
          value={formik.values.iban}
          setValue={(newIban) => formik.setFieldValue('iban', newIban)}
          error={
            getIn(formik.touched, 'iban')
              ? getIn(formik.errors, 'iban')
              : undefined
          }
          disabled={isFormDisabled}
        />
      </Stack>
      <Typography
        variant="subtitle2"
        align="left"
        sx={{
          color: 'grey.500',
          marginY: 2,
        }}
      >
        {t('mobile.family-member.mandate-agreement-text')}
      </Typography>
      <Stack
        sx={{
          alignItems: 'flex-end',
        }}
      >
        <Button
          type="submit"
          onClick={() => formik.handleSubmit()}
          id="activate-direct-debit-button"
          variant="contained"
          disabled={!formik.isValid || isFormDisabled}
          loading={mandateStatus === MandateStatus.Loading}
        >
          {t('mobile.family-member.confirm-mandate-button')}
        </Button>
      </Stack>
    </RootContainer>
  );
};

const RootContainer = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
  })
);
